import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CandidateNomination, Election, Page, PollingStation, StationTally, TallyStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const TALLY_ROLES = ['RETURNING_OFFICER', 'ELECTORAL_COMMISSIONER'];
const SUBMIT_ROLES = ['RETURNING_OFFICER'];
const STATUSES: TallyStatus[] = ['DRAFT', 'SUBMITTED', 'VERIFIED', 'DISPUTED'];

/** GET/POST /api/v1/m39/tallies — station-level ballot accounting; dual-control verification. */
export function Tallies() {
  const { persona } = useAuth();
  const canView = persona?.roles.some((r) => TALLY_ROLES.includes(r)) ?? false;
  const canSubmit = persona?.roles.some((r) => SUBMIT_ROLES.includes(r)) ?? false;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [electionId] = useState(params.get('electionId') ?? '');
  const [status, setStatus] = useState<TallyStatus | ''>('');
  const [showCreate, setShowCreate] = useState(false);

  const qp = new URLSearchParams({ limit: '50' });
  if (status) qp.set('status', status);
  if (electionId) qp.set('electionId', electionId);
  const listQ = useApiQuery<Page<StationTally>>(canView ? `/api/v1/m39/tallies?${qp.toString()}` : null);
  const stationsQ = useApiQuery<Page<PollingStation>>('/api/v1/m39/polling-stations?status=ACTIVE&limit=100');
  const stationName = (id: string) => stationsQ.data?.items.find((s) => s.id === id)?.name ?? id;

  return (
    <div>
      <PageHeader
        title="Station Tallies"
        subtitle="Ballot accounting per polling station, with dual-control (four-eyes) verification."
        actions={
          canSubmit ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Submit tally
            </button>
          ) : undefined
        }
      />
      {!canView ? <RoleNotice roles={TALLY_ROLES} /> : null}

      {canView ? (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <Field label="Status" className="!mb-0">
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as TallyStatus | '')}>
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            {electionId ? <span className="text-xs text-slate-400">Filtered to election {electionId}</span> : null}
          </div>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No tallies match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Station</th>
                    <th className="table-head">Registered</th>
                    <th className="table-head">Valid votes</th>
                    <th className="table-head">Rejected</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((t) => (
                    <tr key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/elections/tallies/${t.id}`)}>
                      <td className="table-cell font-medium">{stationName(t.pollingStationId)}</td>
                      <td className="table-cell text-xs tabular-nums">{t.registeredVoters}</td>
                      <td className="table-cell text-xs tabular-nums">{t.validVotes}</td>
                      <td className="table-cell text-xs tabular-nums">{t.rejectedVotes}</td>
                      <td className="table-cell">
                        <StatusBadge status={t.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {showCreate ? (
        <SubmitTallyModal
          defaultElectionId={electionId}
          onClose={() => setShowCreate(false)}
          onDone={(created) => {
            setShowCreate(false);
            navigate(`/elections/tallies/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function SubmitTallyModal({ defaultElectionId, onClose, onDone }: { defaultElectionId: string; onClose: () => void; onDone: (t: StationTally) => void }) {
  const [electionId, setElectionId] = useState(defaultElectionId);
  const [pollingStationId, setPollingStationId] = useState('');
  const [registeredVoters, setRegisteredVoters] = useState('');
  const [ballotsIssued, setBallotsIssued] = useState('');
  const [validVotes, setValidVotes] = useState('');
  const [rejectedVotes, setRejectedVotes] = useState('');
  const [candidateVotesText, setCandidateVotesText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const electionsQ = useApiQuery<Page<Election>>('/api/v1/m39/elections?status=COUNTING&limit=50');
  const stationsQ = useApiQuery<Page<PollingStation>>('/api/v1/m39/polling-stations?status=ACTIVE&limit=100');
  // Only REGISTRATION_OFFICER/ELECTORAL_COMMISSIONER can list nominations
  // (NOMINATION_DECISION_ROLES) — a RETURNING_OFFICER submitting a tally
  // cannot, so this lookup is attempted best-effort and silently falls back
  // to raw nominationId entry (paired with the printed ballot form's
  // candidate order in a real deployment) when it 403s. Flagged in this
  // build's report as a genuine cross-role capability gap, not invented here.
  const nominationsQ = useApiQuery<Page<CandidateNomination>>(
    electionId ? `/api/v1/m39/nominations?electionId=${encodeURIComponent(electionId)}&status=ACCEPTED&limit=100` : null,
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const candidateVotes = candidateVotesText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [nominationId, votes] = line.split(',').map((s) => s.trim());
          return { nominationId, votes: Number.parseInt(votes ?? '0', 10) };
        });
      const created = await api.post<StationTally>('/api/v1/m39/tallies', {
        electionId,
        pollingStationId,
        registeredVoters: Number.parseInt(registeredVoters, 10),
        ballotsIssued: Number.parseInt(ballotsIssued, 10),
        validVotes: Number.parseInt(validVotes, 10),
        rejectedVotes: Number.parseInt(rejectedVotes, 10),
        candidateVotes,
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit tally');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    electionId.length > 0 &&
    pollingStationId.length > 0 &&
    [registeredVoters, ballotsIssued, validVotes, rejectedVotes].every((v) => v.trim().length > 0 && Number.isFinite(Number(v)));

  return (
    <Modal title="Submit a station tally" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Election (COUNTING only)">
            <select className="field-input" value={electionId} onChange={(e) => setElectionId(e.target.value)}>
              <option value="">Select…</option>
              {(electionsQ.data?.items ?? []).map((el) => (
                <option key={el.id} value={el.id}>
                  {el.title}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Polling station">
            <select className="field-input" value={pollingStationId} onChange={(e) => setPollingStationId(e.target.value)}>
              <option value="">Select…</option>
              {(stationsQ.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Registered voters">
            <input type="number" className="field-input" value={registeredVoters} onChange={(e) => setRegisteredVoters(e.target.value)} />
          </Field>
          <Field label="Ballots issued">
            <input type="number" className="field-input" value={ballotsIssued} onChange={(e) => setBallotsIssued(e.target.value)} />
          </Field>
          <Field label="Valid votes">
            <input type="number" className="field-input" value={validVotes} onChange={(e) => setValidVotes(e.target.value)} />
          </Field>
          <Field label="Rejected votes">
            <input type="number" className="field-input" value={rejectedVotes} onChange={(e) => setRejectedVotes(e.target.value)} />
          </Field>
        </div>

        {nominationsQ.data && nominationsQ.data.items.length > 0 ? (
          <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-500">
            Accepted candidates for this election: {nominationsQ.data.items.map((n) => `${n.candidateName} = ${n.id}`).join(' · ')}
          </div>
        ) : nominationsQ.error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Could not look up accepted candidates ({nominationsQ.error}) — GET /nominations is Registration Officer/Commissioner-only. Enter
            nominationId,votes pairs from the printed ballot form below.
          </div>
        ) : null}
        <Field label="Candidate votes — one “nominationId,votes” pair per line">
          <textarea
            className="field-input font-mono text-xs"
            rows={4}
            value={candidateVotesText}
            onChange={(e) => setCandidateVotesText(e.target.value)}
            placeholder={'cl9abc123,412\ncl9def456,388'}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Submitting…' : 'Submit tally'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
